/**
 * Phase 3.68 — Warenwirtschaft Section (B2B Dashboard).
 *
 * 4 Sub-Akkordeons:
 *   1) Warenbestand hochladen (CSV-Import, nutzt /api/import/listings)
 *   2) KI-Update via Hersteller-Seite (MOCKED)
 *   3) KI-Update via Internet (MOCKED)
 *   4) eBay-Sync (Credentials + Push, MOCKED)
 */
import { useEffect, useState } from "react";
import { Boxes, Upload, Sparkles, Globe, ShoppingCart, Loader2, ShieldAlert, Save, Send, CheckCircle2, Network, Share2, Truck, MapPin, ClipboardCheck, Wand2, Percent, Settings } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import StorageLocationsPanel from "./StorageLocationsPanel";
import InventoryCapturePanel from "./InventoryCapturePanel";
import BatchAiEnrichPanel from "./BatchAiEnrichPanel";
import ProposalReviewerPanel from "./ProposalReviewerPanel";
import MarkupRulesPanel from "../wms/MarkupRulesPanel";
import BranchOverrideToggle from "./BranchOverrideToggle";
import OrgLockedSection from "./OrgLockedSection";
import { DiscountRulesBlock } from "./VoucherDiscountSettings";
import useSettingsExpandAll from "../../lib/useSettingsExpandAll";
import WmsSettingsModal from "./WmsSettingsModal";

function MockBanner({ children }) {
    return (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-3 text-xs flex items-start gap-2" data-testid="warenwirtschaft-mock-banner">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{children}</span>
        </div>
    );
}

export function UploadPanel() {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const doPreview = async () => {
        if (!file) { toast.error("Bitte Datei wählen"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/import/listings/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(r.data);
            toast.success(`${r.data.valid_rows ?? r.data.count ?? 0} gültige Zeilen erkannt`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };
    const doCommit = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/import/listings/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${r.data.created_count ?? 0} Inserate erstellt`);
            setPreview(null);
            setFile(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3" data-testid="warenwirtschaft-upload-panel">
            <p className="text-xs text-gray-600">
                Laden Sie eine CSV-Datei (UTF-8, Semikolon-getrennt) mit Ihrem Warenbestand hoch. Pflichtfelder: <code>title;brand;model;price;year;category;mileage;description;location</code>.
            </p>
            <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[200px]">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">CSV-Datei</Label>
                    <Input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                        className="rounded-sm mt-1"
                        data-testid="warenwirtschaft-upload-file"
                    />
                </div>
                <Button onClick={doPreview} disabled={busy || !file} variant="outline" className="rounded-sm" data-testid="warenwirtschaft-upload-preview">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Vorschau
                </Button>
                <Button onClick={doCommit} disabled={busy || !preview} className="bg-[#111827] hover:bg-black text-white rounded-sm" data-testid="warenwirtschaft-upload-commit">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Import bestätigen
                </Button>
            </div>
            {preview && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-xs text-gray-700" data-testid="warenwirtschaft-upload-preview-info">
                    <strong>{preview.valid_rows ?? preview.count ?? 0}</strong> gültige Zeilen ·{" "}
                    <strong>{preview.error_rows ?? 0}</strong> Fehler-Zeilen erkannt. Klicken Sie auf „Import bestätigen", um die Inserate anzulegen.
                </div>
            )}
        </div>
    );
}

function AiEnrichPanel({ source, testIdPrefix, icon: Icon, description }) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const endpoint = source === "manufacturer"
        ? "/warenwirtschaft/ai-enrich/manufacturer"
        : "/warenwirtschaft/ai-enrich/internet";

    const run = async () => {
        setBusy(true);
        try {
            const r = await api.post(endpoint, {});
            setResult(r.data);
            toast.success(`KI-Update fertig: ${r.data.updated_count} Inserate aktualisiert`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "KI-Update fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3" data-testid={`${testIdPrefix}-panel`}>
            <MockBanner>
                MOCKED — KI-Anbindung wird mit Gemini Nano Banana (Bilder) + Gemini 3 Pro (Texte/Recherche) live geschaltet, sobald die API-Keys hinterlegt sind. Aktuell ergänzt der Service plausible Demo-Daten und kennzeichnet sie als KI-generiert.
            </MockBanner>
            <p className="text-xs text-gray-600">{description}</p>
            <div className="flex items-center gap-2">
                <Button
                    onClick={run}
                    disabled={busy}
                    className="bg-[#111827] hover:bg-black text-white rounded-sm"
                    data-testid={`${testIdPrefix}-run`}
                >
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Jetzt mit KI anreichern
                </Button>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-50 border border-violet-200 px-2 py-1 rounded-sm">
                    <Sparkles className="w-3 h-3" /> KI-Kennzeichnung aktiv
                </span>
            </div>
            {result && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs" data-testid={`${testIdPrefix}-result`}>
                    <div className="font-bold text-emerald-900 flex items-center gap-1.5 mb-1">
                        <CheckCircle2 className="w-4 h-4" /> {result.updated_count} Inserate erfolgreich erweitert
                    </div>
                    <ul className="space-y-0.5 text-emerald-900">
                        {(result.items || []).slice(0, 5).map((it) => (
                            <li key={it.id}>
                                ✨ <strong>{it.title}</strong>
                                {it.added_fields?.length ? <span className="opacity-70"> — {it.added_fields.join(", ")}</span> : null}
                            </li>
                        ))}
                        {(result.items || []).length > 5 && (
                            <li className="opacity-70">… und {result.items.length - 5} weitere</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

function EbayPanel({ currentUser }) {
    const { refresh } = useAuth();
    const [form, setForm] = useState({
        ebay_sync_global_enabled: !!currentUser.ebay_sync_global_enabled,
        ebay_username: currentUser.ebay_username || "",
        ebay_dev_id: currentUser.ebay_dev_id || "",
        ebay_app_id: currentUser.ebay_app_id || "",
        ebay_cert_id: currentUser.ebay_cert_id || "",
        ebay_auth_token: currentUser.ebay_auth_token || "",
    });
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadStatus = async () => {
        try {
            const r = await api.get("/warenwirtschaft/ebay/status");
            setStatus(r.data);
        } catch { /* ignore */ }
    };
    useEffect(() => { loadStatus(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/warenwirtschaft/ebay/credentials", form);
            toast.success("eBay-Einstellungen gespeichert");
            await refresh();
            await loadStatus();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const syncNow = async () => {
        setBusy(true);
        try {
            const r = await api.post("/warenwirtschaft/ebay/sync", {});
            toast.success(`${r.data.synced_count} Inserate an eBay gepusht (MOCKED)`);
            await loadStatus();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sync fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3" data-testid="warenwirtschaft-ebay-panel">
            <MockBanner>
                MOCKED — eBay-Trading-API geht live, sobald Sie unten die Zugangsdaten hinterlegen UND wir die eBay-Sandbox-Keys auf die Produktionsumgebung umstellen. Aktuell werden Inserate intern als „ebay_synced" markiert; es findet kein echter Push statt.
            </MockBanner>
            <label className="flex items-start gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="ebay-global-toggle-wrap">
                <input
                    type="checkbox"
                    checked={!!form.ebay_sync_global_enabled}
                    onChange={(e) => setForm({ ...form, ebay_sync_global_enabled: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-[#111827]"
                    data-testid="ebay-global-toggle"
                />
                <div>
                    <div className="font-bold text-sm">eBay-Sync global aktivieren</div>
                    <div className="text-[11px] text-gray-600">Inserate mit gesetzter Markierung „An eBay übergeben" werden automatisch synchronisiert.</div>
                </div>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">eBay-Benutzername</Label>
                    <Input value={form.ebay_username} onChange={(e) => setForm({ ...form, ebay_username: e.target.value })} className="rounded-sm mt-1" data-testid="ebay-username" />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Dev ID</Label>
                    <Input value={form.ebay_dev_id} onChange={(e) => setForm({ ...form, ebay_dev_id: e.target.value })} className="rounded-sm mt-1 font-mono-num" data-testid="ebay-dev-id" />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">App ID</Label>
                    <Input value={form.ebay_app_id} onChange={(e) => setForm({ ...form, ebay_app_id: e.target.value })} className="rounded-sm mt-1 font-mono-num" data-testid="ebay-app-id" />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Cert ID</Label>
                    <Input value={form.ebay_cert_id} onChange={(e) => setForm({ ...form, ebay_cert_id: e.target.value })} className="rounded-sm mt-1 font-mono-num" data-testid="ebay-cert-id" />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Auth Token</Label>
                    <Input value={form.ebay_auth_token} onChange={(e) => setForm({ ...form, ebay_auth_token: e.target.value })} className="rounded-sm mt-1 font-mono-num" data-testid="ebay-auth-token" placeholder="A1...sehr-lang..." />
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <Button onClick={save} disabled={busy} className="bg-[#111827] hover:bg-black text-white rounded-sm" data-testid="ebay-save">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Zugangsdaten speichern
                </Button>
                <Button onClick={syncNow} disabled={busy || !status?.configured} variant="outline" className="rounded-sm border-[#111827] text-[#111827]" data-testid="ebay-sync-now">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Jetzt an eBay übergeben
                </Button>
                {status && (
                    <span className="text-[11px] text-gray-600" data-testid="ebay-status-line">
                        Status: {status.configured ? <strong className="text-emerald-700">Zugangsdaten gesetzt</strong> : <em className="text-amber-700">Zugangsdaten fehlen</em>} · {status.eligible_listings} Inserate markiert
                    </span>
                )}
            </div>
            <p className="text-[11px] text-gray-500">
                Hinweis: Markieren Sie einzelne Inserate beim Bearbeiten („Werbung & Reichweite" → „An eBay übergeben"), um sie für den Sync vorzubereiten.
            </p>
        </div>
    );
}

function NetworkPanel({ type, currentUser }) {
    const { refresh } = useAuth();
    const [busy, setBusy] = useState(false);
    const labelMap = {
        net_browse: { key: "inventory_network_browse_enabled", title: "Auf Lagerbestand anderer Händler zugreifen", hint: "Aktivieren Sie diese Option, um Inserate kooperierender Händler in Ihrer eigenen Suche & im Inserieren-Flow durchsuchen zu können." },
        net_share: { key: "inventory_network_share_enabled", title: "Eigenes Lager für andere Händler freigeben", hint: "Andere registrierte Händler dürfen Ihren Lagerbestand sehen und ggf. weiterverkaufen (B2B-Sichtbarkeit auf Inserat-Ebene bleibt erhalten)." },
        net_supplier: { key: "inventory_supplier_integration_enabled", title: "Lieferanten-Lager einbinden", hint: "Verbinden Sie externe Großhändler / Hersteller-Lagerbestände (z.B. Importeur, Distributor) — Bestand wird automatisch synchronisiert." },
    };
    const cfg = labelMap[type];
    const checked = !!currentUser?.[cfg.key];

    const save = async (nextVal) => {
        setBusy(true);
        try {
            await api.patch("/users/me/preferences", { [cfg.key]: nextVal });
            toast.success("Einstellung gespeichert");
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3" data-testid={`warenwirtschaft-${type}-panel`}>
            <MockBanner>
                MOCKED — Die Lagernetzwerk-Funktion ist als Opt-in-Toggle vorbereitet. Die eigentliche Cross-Dealer-Datenfreigabe wird in einem Folge-Sprint aktiviert (DSGVO-Konsens, Zugriffslogs, Cross-Sell-Provisionen werden konfiguriert).
            </MockBanner>
            <label className="flex items-start gap-3 cursor-pointer bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid={`warenwirtschaft-${type}-toggle-wrap`}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => save(e.target.checked)}
                    disabled={busy}
                    className="mt-0.5 w-5 h-5 accent-[#111827]"
                    data-testid={`warenwirtschaft-${type}-toggle`}
                />
                <div>
                    <div className="font-bold text-sm">{cfg.title}</div>
                    <div className="text-[11px] text-gray-600 mt-0.5">{cfg.hint}</div>
                </div>
            </label>
        </div>
    );
}

export function WarenwirtschaftSection({ currentUser }) {
    const { refresh } = useAuth();
    const [expanded, setExpanded] = useState(false);
    const [showWmsSettings, setShowWmsSettings] = useState(false);
    useSettingsExpandAll(setExpanded);
    const [open, setOpen] = useState({ markup: false, upload: false, ai_man: false, ai_net: false, ebay: false, net_browse: false, net_share: false, net_supplier: false, locations: false, inventory: false, batch_ai: false, proposals: false });
    const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

    // Phase 4g.R — Status-Badges: laufender KI-Batch + Anzahl erfasster Inventuren.
    const [batchRunning, setBatchRunning] = useState(null); // {processed,total} | null
    const [batchCompleted, setBatchCompleted] = useState(0); // Anzahl abgeschlossener Läufe
    const [inventoryCount, setInventoryCount] = useState(0);
    useEffect(() => {
        if (!expanded) return undefined;
        let alive = true;
        const fetchStatus = async () => {
            try {
                const [bj, inv] = await Promise.all([
                    api.get("/articles/ai-enrich/batch", { params: { limit: 20 } }).catch(() => ({ data: { jobs: [] } })),
                    api.get("/inventory-sessions", { params: { limit: 100 } }).catch(() => ({ data: [] })),
                ]);
                if (!alive) return;
                const jobs = bj.data?.jobs || [];
                const running = jobs.find((j) => j.status === "running");
                setBatchRunning(running ? { processed: running.processed || 0, total: running.total || 0 } : null);
                setBatchCompleted(jobs.filter((j) => j.status === "completed").length);
                setInventoryCount(Array.isArray(inv.data) ? inv.data.length : 0);
            } catch { /* ignore */ }
        };
        fetchStatus();
        const h = setInterval(fetchStatus, 15000);
        return () => { alive = false; clearInterval(h); };
    }, [expanded]);

    // "Anhaken" — pure on/off marker per workflow. Saved as user preference
    // via the existing /users/me/preferences endpoint so the state survives
    // reload + is shared across the dealer team.
    const enabledMap = {
        upload: "ww_upload_enabled",
        ai_man: "ww_ai_man_enabled",
        ai_net: "ww_ai_net_enabled",
        ebay: "ww_ebay_enabled",
        net_browse: "inventory_network_browse_enabled",
        net_share: "inventory_network_share_enabled",
        net_supplier: "inventory_supplier_integration_enabled",
    };
    const setEnabled = async (id, val) => {
        const key = enabledMap[id];
        try {
            await api.patch("/users/me/preferences", { [key]: val });
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    const SectionHeader = ({ id, icon: Icon, title, hint }) => {
        const checked = !!currentUser?.[enabledMap[id]];
        return (
            <div
                className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200"
                data-testid={`warenwirtschaft-row-${id}`}
            >
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => { e.stopPropagation(); setEnabled(id, e.target.checked); }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 accent-[#111827] shrink-0"
                    data-testid={`warenwirtschaft-enable-${id}`}
                    aria-label={`${title} aktivieren`}
                />
                <button
                    type="button"
                    onClick={() => toggle(id)}
                    className="flex-1 min-w-0 flex items-center gap-3 text-left hover:opacity-80"
                    data-testid={`warenwirtschaft-toggle-${id}`}
                >
                    <Icon className="w-4 h-4 text-[#FFCC00] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-[#111827]">{title}</div>
                        <div className="text-[11px] text-gray-500">{hint}</div>
                    </div>
                    <span className={`text-gray-400 text-lg transition-transform ${open[id] ? "rotate-180" : ""}`}>⌄</span>
                </button>
            </div>
        );
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="warenwirtschaft-section">
            <div className={`flex items-stretch ${expanded ? "border-b border-gray-200" : ""}`}>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex-1 min-w-0 flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                data-testid="warenwirtschaft-toggle"
                aria-expanded={expanded}
            >
                <Boxes className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold">Warenwirtschaft</h2>
                    <p className="text-xs text-gray-600 mt-1">
                        Warenbestand verwalten — Massen-Upload, KI-gestützte Anreicherung von Hersteller- & Internet-Quellen, eBay-Sync.
                    </p>
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            {/* Jul-2026 (User-Wunsch) — Zahnrad rechts von „Warenwirtschaft": Lieferschein-Scan-Regeln + Artikeldaten-Anreicherung */}
            <div className="flex items-center pr-4 pl-1 shrink-0">
                <button
                    type="button"
                    onClick={() => setShowWmsSettings(true)}
                    className="p-2 rounded-sm border border-gray-300 text-gray-500 hover:text-[#111827] hover:border-[#111827] transition-colors"
                    title="Warenwirtschaft-Einstellungen (Lieferschein-Regeln & Artikeldaten-Anreicherung)"
                    data-testid="wms-settings-gear"
                >
                    <Settings className="w-4 h-4" />
                </button>
            </div>
            </div>
            <WmsSettingsModal open={showWmsSettings} onClose={() => setShowWmsSettings(false)} />
            {expanded && (
                <div data-testid="warenwirtschaft-body">
                    {/* VK-Kalkulation — Jun-2026 (User-Wunsch): Bearbeitung der Aufschläge
                        + kategorie-basierten Abschläge lebt hier (in der WMS nur read-only). */}
                    <div className="flex items-stretch border-b border-gray-200 bg-gray-50">
                    <button
                        type="button"
                        onClick={() => toggle("markup")}
                        className="flex-1 min-w-0 flex items-center gap-3 px-5 py-3 hover:bg-gray-100 text-left"
                        data-testid="warenwirtschaft-toggle-markup"
                    >
                        <Percent className="w-4 h-4 text-[#FFCC00] shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-[#111827]">VK-Kalkulation (% Aufschläge &amp; Abschläge)</div>
                            <div className="text-[11px] text-gray-500">Aufschlags-Regeln, Kommissions-Abschlag &amp; interne Abschläge je Kategorie</div>
                        </div>
                        <span className={`text-gray-400 text-lg transition-transform ${open.markup ? "rotate-180" : ""}`}>⌄</span>
                    </button>
                    <div className="flex items-center pr-4 pl-2 shrink-0">
                        <BranchOverrideToggle area="vk_kalkulation" currentUser={currentUser}
                            infoText="VK-Kalkulation (Aufschlags-/Abschlagsregeln) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                            testId="vk-kalkulation-branch-override" />
                    </div>
                    </div>
                    {open.markup && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-markup-body">
                            <OrgLockedSection area="vk_kalkulation" label="Die VK-Kalkulation">
                                <MarkupRulesPanel />
                                {/* Jul-2026 (User-Wunsch) — Rabatt-Regeln (Höchstsätze) hierher verschoben */}
                                <div className="mt-4">
                                    <DiscountRulesBlock />
                                </div>
                            </OrgLockedSection>
                        </div>
                    )}
                    {/* Lagerorte — Phase 4e: ohne Enable-Checkbox (Kernfunktion, immer verfügbar) */}
                    <button
                        type="button"
                        onClick={() => toggle("locations")}
                        className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 text-left"
                        data-testid="warenwirtschaft-toggle-locations"
                    >
                        <MapPin className="w-4 h-4 text-[#FFCC00] shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-[#111827]">Lagerorte &amp; QR-Etiketten</div>
                            <div className="text-[11px] text-gray-500">Lagerort-Stammdaten + druckbare QR-Etiketten für die Inventur</div>
                        </div>
                        <span className={`text-gray-400 text-lg transition-transform ${open.locations ? "rotate-180" : ""}`}>⌄</span>
                    </button>
                    {open.locations && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-locations-body">
                            <StorageLocationsPanel />
                        </div>
                    )}
                    {/* Inventur-Erfassung — Phase 4e.B */}
                    <button
                        type="button"
                        onClick={() => toggle("inventory")}
                        className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 text-left"
                        data-testid="warenwirtschaft-toggle-inventory"
                    >
                        <ClipboardCheck className="w-4 h-4 text-[#FFCC00] shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-[#111827] flex items-center gap-2">
                                Inventur erfassen
                                {inventoryCount > 0 && (
                                    <span className="inline-block px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white text-[9px] font-bold uppercase tracking-wider" data-testid="ww-inventory-badge">
                                        {inventoryCount} erfasst
                                    </span>
                                )}
                            </div>
                            <div className="text-[11px] text-gray-500">Pflicht: Lagerort-Scan + bis 6 Foto-Belege pro Artikel · Live-Feed der letzten Buchungen</div>
                        </div>
                        <span className={`text-gray-400 text-lg transition-transform ${open.inventory ? "rotate-180" : ""}`}>⌄</span>
                    </button>
                    {open.inventory && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-inventory-body">
                            <InventoryCapturePanel />
                        </div>
                    )}
                    {/* Batch-KI-Lauf — Phase 4e.D++ */}
                    <button
                        type="button"
                        onClick={() => toggle("batch_ai")}
                        className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 text-left"
                        data-testid="warenwirtschaft-toggle-batch-ai"
                    >
                        <Wand2 className="w-4 h-4 text-[#FFCC00] shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-[#111827] flex items-center gap-2">
                                KI-Batch-Anreicherung
                                {batchRunning ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-500 text-[#111827] text-[9px] font-bold uppercase tracking-wider animate-pulse" data-testid="ww-batch-running-badge">
                                        <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                        läuft {batchRunning.processed}/{batchRunning.total}
                                    </span>
                                ) : batchCompleted > 0 ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white text-[9px] font-bold uppercase tracking-wider" data-testid="ww-batch-done-badge">
                                        <CheckCircle2 className="w-2.5 h-2.5" />
                                        {batchCompleted} {batchCompleted === 1 ? "Lauf" : "Läufe"} fertig
                                    </span>
                                ) : null}
                            </div>
                            <div className="text-[11px] text-gray-500">Bis zu 200 Artikel auf einmal recherchieren · Live-Progress-Sidebar</div>
                        </div>
                        <span className={`text-gray-400 text-lg transition-transform ${open.batch_ai ? "rotate-180" : ""}`}>⌄</span>
                    </button>
                    {open.batch_ai && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-batch-ai-body">
                            <BatchAiEnrichPanel />
                        </div>
                    )}
                    {/* Proposal-Reviewer — Phase 4e.G */}
                    <button
                        type="button"
                        onClick={() => toggle("proposals")}
                        className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 text-left"
                        data-testid="warenwirtschaft-toggle-proposals"
                    >
                        <Sparkles className="w-4 h-4 text-[#FFCC00] shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-[#111827]">KI-Vorschläge zur Prüfung</div>
                            <div className="text-[11px] text-gray-500">Inbox aller pending KI-Recherche-Vorschläge · Inline-Apply</div>
                        </div>
                        <span className={`text-gray-400 text-lg transition-transform ${open.proposals ? "rotate-180" : ""}`}>⌄</span>
                    </button>
                    {open.proposals && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-proposals-body">
                            <ProposalReviewerPanel />
                        </div>
                    )}
                    {/* „Warenbestand hochladen (CSV-Massen-Import)" wurde Jun-2026
                        (User-Wunsch) in den Bereich „Import / Export / Schnittstellen"
                        → Unterpunkt „Warenbestand" verschoben. */}
                    <SectionHeader id="ai_man" icon={Sparkles} title="Hersteller-KI: aktualisieren & bebildern" hint="Daten + Bilder via Hersteller-Quellen (Gemini)" />
                    {open.ai_man && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-ai-man-body">
                            <AiEnrichPanel
                                source="manufacturer"
                                testIdPrefix="ai-manufacturer"
                                icon={Sparkles}
                                description="Die KI durchsucht die Hersteller-Webseite (z. B. bmw-motorrad.de) und ergänzt fehlende Beschreibungen, Ausstattung & generiert ggf. Standardbilder. Alle von der KI ergänzten Felder werden mit ✨ KI-generiert markiert."
                            />
                        </div>
                    )}
                    <SectionHeader id="ai_net" icon={Globe} title="Internet-KI: aktualisieren & bebildern" hint="Daten + Bilder via öffentlicher Suche (Gemini)" />
                    {open.ai_net && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-ai-net-body">
                            <AiEnrichPanel
                                source="internet"
                                testIdPrefix="ai-internet"
                                icon={Globe}
                                description="Die KI durchsucht öffentliche Quellen (Foren, Tests, Bewertungen) und ergänzt Beschreibungen, Zubehör-Listen & Marktwert-Hinweise. Alle KI-Ergänzungen werden klar gekennzeichnet."
                            />
                        </div>
                    )}
                    <SectionHeader id="ebay" icon={ShoppingCart} title="An eBay übergeben" hint="Zugangsdaten + manueller / automatischer Sync" />
                    {open.ebay && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-ebay-body">
                            <EbayPanel currentUser={currentUser} />
                        </div>
                    )}
                    <SectionHeader id="net_browse" icon={Network} title="Lagerbestand anderer Händler nutzen" hint="Inserate kooperierender Händler durchsuchen & ggf. weiterverkaufen" />
                    {open.net_browse && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-net-browse-body">
                            <NetworkPanel type="net_browse" currentUser={currentUser} />
                        </div>
                    )}
                    <SectionHeader id="net_share" icon={Share2} title="Eigenes Lager freigeben" hint="Andere Händler dürfen Ihren Bestand sehen und ggf. weiterverkaufen" />
                    {open.net_share && (
                        <div className="p-5 border-b border-gray-200" data-testid="warenwirtschaft-net-share-body">
                            <NetworkPanel type="net_share" currentUser={currentUser} />
                        </div>
                    )}
                    <SectionHeader id="net_supplier" icon={Truck} title="Lieferanten-Lager einbinden" hint="Großhändler / Importeur / Distributor anbinden — Bestand auto-sync" />
                    {open.net_supplier && (
                        <div className="p-5" data-testid="warenwirtschaft-net-supplier-body">
                            <NetworkPanel type="net_supplier" currentUser={currentUser} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
